import type { YMarketOrder } from '@/lib/types'
import type { TemplateData } from '@/lib/types'
import type { BuyerInfo } from '@/lib/orders-store'
import type { CustomerShipmentState } from '@/lib/customer-state'
import { formatIsoDateFull } from '@/lib/utils'
import { YMarketOrderStatus } from '@/lib/ymarket-domain'

export const EM_DASH = '—'

export const STATUSES: { key: string; label: string; color: string }[] = [
  { key: YMarketOrderStatus.Delivery, label: 'Доставка', color: 'var(--color-st-delivery)' },
  { key: YMarketOrderStatus.Processing, label: 'Обработка', color: 'var(--color-st-processing)' },
  { key: YMarketOrderStatus.Delivered, label: 'Доставлен', color: 'var(--color-st-delivered)' },
  { key: YMarketOrderStatus.Pickup, label: 'Самовывоз', color: 'var(--color-st-pickup)' },
  { key: YMarketOrderStatus.Unpaid, label: 'Не оплачен', color: 'var(--color-st-unpaid)' },
  { key: YMarketOrderStatus.Cancelled, label: 'Отменён', color: 'var(--color-st-unpaid)' },
  { key: YMarketOrderStatus.Returned, label: 'Возвращён', color: 'var(--color-st-unpaid)' },
  { key: YMarketOrderStatus.PartiallyReturned, label: 'Частично возвращён', color: 'var(--color-st-unpaid)' },
]

export const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  STATUSES.map((s) => [s.key, s.label])
)

const UNKNOWN_SUBSTATUS_LABEL = 'Подстатус не распознан'

export interface SubstatusLabelDefinition {
  label: string
  meaning: string
}

const UNKNOWN_SUBSTATUS_DEFINITION: SubstatusLabelDefinition = {
  label: UNKNOWN_SUBSTATUS_LABEL,
  meaning: UNKNOWN_SUBSTATUS_LABEL,
}

export const SUBSTATUS_LABELS: Record<string, SubstatusLabelDefinition> = {
  STARTED: {
    label: 'Подтверждён, можно обрабатывать',
    meaning: 'Заказ подтвержден, его можно начать обрабатывать.',
  },
  READY_TO_SHIP: {
    label: 'Собран, готов к отправке',
    meaning: 'Заказ собран и готов к отправке.',
  },
  SHIPPED: {
    label: 'Передан службе доставки',
    meaning: 'Заказ передан службе доставки.',
  },
  RESERVATION_EXPIRED: {
    label: 'Оформление не завершено',
    meaning: 'Покупатель не завершил оформление зарезервированного заказа в течение 10 минут.',
  },
  USER_NOT_PAID: {
    label: 'Не оплачен покупателем',
    meaning: 'Покупатель не оплатил заказ при предоплате в течение 30 минут.',
  },
  USER_UNREACHABLE: {
    label: 'Покупатель недоступен',
    meaning: 'Не удалось связаться с покупателем. Для отмены с этой причиной необходимо выполнить условия: не менее 3 звонков с 8 до 21 в часовом поясе покупателя; перерыв между первым и третьим звонком не менее 90 минут; соединение не короче 5 секунд. Если хотя бы одно из этих условий не выполнено (кроме случая, когда номер недоступен), отменить заказ не получится. Вернется ответ с кодом ошибки 400.',
  },
  USER_CHANGED_MIND: {
    label: 'Отмена по личным причинам',
    meaning: 'Покупатель отменил заказ по личным причинам.',
  },
  USER_REFUSED_DELIVERY: {
    label: 'Не устроила доставка',
    meaning: 'Покупателя не устроили условия доставки.',
  },
  USER_REFUSED_PRODUCT: {
    label: 'Товар не подошел',
    meaning: 'Покупателю не подошел товар.',
  },
  SHOP_FAILED: {
    label: 'Магазин не может выполнить',
    meaning: 'Магазин не может выполнить заказ.',
  },
  USER_REFUSED_QUALITY: {
    label: 'Не устроило качество',
    meaning: 'Покупателя не устроило качество товара.',
  },
  USER_IDENTIFICATION_MISMATCH: {
    label: 'Не совпали данные документа',
    meaning: 'Идентификационный документ покупателя не совпадает с данными в заказе.',
  },
  PURCHASE_GROUP_THRESHOLD_NOT_REACHED_CANCELLED: {
    label: 'Не достигнут порог покупки',
    meaning: 'Заказ участвовал в групповой покупке и был отменен, потому что не было достигнуто нужное количество покупок.',
  },
  REPLACING_ORDER: {
    label: 'Замена товара покупателем',
    meaning: 'Покупатель решил заменить товар другим по собственной инициативе.',
  },
  PROCESSING_EXPIRED: {
    label: 'Значение больше не используется',
    meaning: 'Значение более не используется.',
  },
  PICKUP_EXPIRED: {
    label: 'Истек срок хранения',
    meaning: 'Закончился срок хранения заказа в пункте выдачи.',
  },
  DELIVERY_SERVICE_UNDELIVERED: {
    label: 'Доставка не выполнена',
    meaning: 'Служба доставки не смогла доставить заказ.',
  },
  CANCELLED_COURIER_NOT_FOUND: {
    label: 'Курьер не найден',
    meaning: 'Не удалось найти курьера.',
  },
  USER_WANTS_TO_CHANGE_DELIVERY_DATE: {
    label: 'Другой день доставки',
    meaning: 'Покупатель хочет получить заказ в другой день.',
  },
  RESERVATION_FAILED: {
    label: 'Обработка остановлена Маркетом',
    meaning: 'Маркет не может продолжить дальнейшую обработку заказа.',
  },
  TOO_MANY_DELIVERY_DATE_CHANGES: {
    label: 'Слишком много переносов',
    meaning: 'Заказ переносили слишком много раз.',
  },
  TOO_LONG_DELIVERY: {
    label: 'Слишком долгая доставка',
    meaning: 'Заказ доставляется слишком долго.',
  },
  INCORRECT_PERSONAL_DATA: {
    label: 'Неверные данные получателя',
    meaning: 'Для заказа из-за рубежа указаны неправильные данные получателя, заказ не пройдет проверку на таможне.',
  },
  TECHNICAL_ERROR: {
    label: 'Техническая ошибка Маркета',
    meaning: 'Техническая ошибка на стороне Маркета. Обратитесь в поддержку.',
  },
  USER_RECEIVED: {
    label: 'Получен покупателем',
    meaning: 'Покупатель получил заказ. Передается только при работе через Яндекс Доставку.',
  },
  DELIVERY_USER_NOT_RECEIVED: {
    label: 'Покупатель не получил',
    meaning: 'Срок доставки истек, покупатель не получил заказ. Начинается арбитраж, по результатам которого заказ может быть отменен или переведен в доставленный статус.',
  },
  DELIVERY_SERVICE_RECEIVED: {
    label: 'Передан в доставку',
    meaning: 'Заказ передан в доставку.',
  },
  PICKUP_SERVICE_RECEIVED: {
    label: 'Принят в пункте выдачи',
    meaning: 'Заказ принят в пункте выдачи заказов.',
  },
  DELIVERY_SERVICE_DELIVERED: {
    label: 'Доставлен',
    meaning: 'Заказ доставлен. Передается автоматически при работе через Яндекс Доставку. Если вы доставляете самостоятельно, измените статус после вручения заказа.',
  },
  DELIVERED_USER_NOT_RECEIVED: {
    label: 'Покупатель не получил',
    meaning: 'Срок доставки истек, покупатель не получил заказ. Начинается арбитраж, по результатам которого заказ может быть отменен или остаться в том же статусе.',
  },
  USER_BOUGHT_CHEAPER: UNKNOWN_SUBSTATUS_DEFINITION,
  CUSTOM: UNKNOWN_SUBSTATUS_DEFINITION,
  USER_WANTED_ANOTHER_PAYMENT_METHOD: UNKNOWN_SUBSTATUS_DEFINITION,
  USER_WANTS_TO_CHANGE_ADDRESS: UNKNOWN_SUBSTATUS_DEFINITION,
  USER_FRAUD: UNKNOWN_SUBSTATUS_DEFINITION,
  USER_FORGOT_TO_USE_BONUS: UNKNOWN_SUBSTATUS_DEFINITION,
  DELIVERED_USER_RECEIVED: UNKNOWN_SUBSTATUS_DEFINITION,
  COURIER_RECEIVED: UNKNOWN_SUBSTATUS_DEFINITION,
}

export function substatusLabel(code: string | null | undefined): string | null {
  const normalizedCode = code?.trim()
  if (!normalizedCode) return null
  return SUBSTATUS_LABELS[normalizedCode]?.label ?? UNKNOWN_SUBSTATUS_LABEL
}

export const STATUS_COLORS: Record<string, string> = Object.fromEntries(
  STATUSES.map((s) => [s.key, s.color])
)

export const CDEK_STATUS_TONES: Record<string, string> = {
  CREATED: 'bg-bone text-ink-muted border-rule-strong',
  RECEIVED_AT_SENDER_WAREHOUSE: 'bg-st-delivery/10 text-st-delivery border-st-delivery/30',
  READY_FOR_SHIPMENT_IN_SENDER_CITY: 'bg-st-delivery/10 text-st-delivery border-st-delivery/30',
  ACCEPTED: 'bg-st-delivery/10 text-st-delivery border-st-delivery/30',
  ACCEPTED_AT_TRANSIT_WAREHOUSE: 'bg-st-processing/10 text-st-processing border-st-processing/30',
  ACCEPTED_AT_RECIPIENT_CITY_WAREHOUSE: 'bg-st-processing/10 text-st-processing border-st-processing/30',
  IN_TRANSIT: 'bg-st-processing/10 text-st-processing border-st-processing/30',
  DELIVERED: 'bg-st-delivered/10 text-st-delivered border-st-delivered/30',
  NOT_DELIVERED: 'bg-st-unpaid/10 text-st-unpaid border-st-unpaid/30',
  INVALID: 'bg-st-unpaid/10 text-st-unpaid border-st-unpaid/30',
}

export interface ManualFields {
  track: string
  transitDays: string
  deliveryDate: string
  shipTime: string
  region: string
  shipDate: string
  firstName: string
}

export function isDbsOrder(o: YMarketOrder): boolean {
  return (o.programType ?? 'DBS') === 'DBS'
}

export function paymentTotal(order: YMarketOrder): number | null {
  return order.prices?.payment?.value ?? null
}

/** Prefer complete customer statistics when present, then the bounded list join. */
export function orderShipment(order: YMarketOrder): CustomerShipmentState | undefined {
  return order.customerStats?.shipment || order.shipment
}

export function formatAddress(order: YMarketOrder): string {
  const a = order.delivery?.courier?.address
  if (!a) return EM_DASH
  return [a.city, a.street, a.house, a.apartment ? `кв. ${a.apartment}` : '']
    .filter(Boolean)
    .join(', ')
}

export function getRegionName(order: YMarketOrder): string {
  const region = order.delivery?.courier?.region
  if (!region) return EM_DASH
  let r: typeof region | undefined = region
  while (r) {
    if (r.type === 'REPUBLIC') return r.name || EM_DASH
    r = r.parent
  }
  return region.name || EM_DASH
}

export function getCity(order: YMarketOrder): string {
  return order.delivery?.courier?.address?.city || ''
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export function getNameParts(b: BuyerInfo | null): string[] {
  if (!b) return []
  return [b.lastName, b.firstName, b.middleName].filter(Boolean) as string[]
}

export function getGreetingName(parts: string[], idx: number | null): string {
  if (parts.length === 0) return ''
  const i = idx !== null && idx >= 0 && idx < parts.length ? idx : parts.length - 1
  return capitalize(parts[i])
}

export function formatBuyerName(b: BuyerInfo | null): string {
  if (!b) return EM_DASH
  const parts = getNameParts(b)
  if (parts.length === 0) return EM_DASH
  return parts.map(capitalize).join(' ')
}

/** Prefer the source-qualified durable projection; drawer data is only a fallback. */
/** Buyers type their names in any case; every name is shown with each word capitalised. */
export function displayPersonName(name: string): string {
  return name.replace(/(^|[\s-])(\p{Ll})/gu, (_match, lead: string, letter: string) => lead + letter.toLocaleUpperCase('ru-RU'))
}

export function formatOrderCustomerName(
  order: YMarketOrder,
  buyer: BuyerInfo | null,
): string {
  const durableName = order.customer?.displayName?.trim()
    || order.customer?.firstName?.trim()
  return displayPersonName(durableName || formatBuyerName(buyer))
}

export function formatYmdDate(dateStr?: string): string {
  if (!dateStr) return EM_DASH
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function orderToTemplate(
  order: YMarketOrder,
  buyerName: string,
  manual: ManualFields
): TemplateData {
  const shipDate = manual.shipDate
    ? formatYmdDate(manual.shipDate)
    : formatIsoDateFull(order.creationDate)
  // Delivery timing is customer-visible only after CDEK has accepted the
  // shipment and supplied its own plan. Never derive it from a quote or form.
  const transitDays = manual.transitDays
  const deliveryDate = manual.deliveryDate
  const apiTrack = order.delivery?.tracks?.[0]?.trackCode
  const trackNumber = apiTrack || manual.track || ''
  return {
    recipientName: buyerName || EM_DASH,
    firstName: manual.firstName || buyerName || EM_DASH,
    shipDate,
    shipTime: manual.shipTime || EM_DASH,
    region: manual.region || getRegionName(order),
    cdekNumber: trackNumber,
    transitDays,
    deliveryDate,
  }
}
